
import React, { useState } from 'react';
import { PieChart, Pie, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedCard } from '@/components/ui/AnimatedCard';
import { gasDistribution, COLORS } from '@/data/gas-analytics-data';
import { getTooltipStyle } from '@/components/telemetry/utils/chartUtils';
import { cn } from '@/lib/utils';

const GasDistributionPieChart = () => {
  const [hiddenSectors, setHiddenSectors] = useState<string[]>([]);

  const handleLegendClick = (name: string) => {
    setHiddenSectors(prev => 
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name]
    );
  };

  // Filter the data based on hidden sectors
  const visibleData = gasDistribution.filter(item => !hiddenSectors.includes(item.name));

  return (
    <AnimatedCard 
      animation="fade-up" 
      delay={600}
      className="bg-white dark:bg-gray-800 border-none"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">Gas Usage Distribution (%)</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Enhanced legend with all categories - positioned above the chart */}
        <div className="mb-4">
          <div className="flex flex-wrap justify-center gap-3">
            {gasDistribution.map((entry, index) => (
              <div 
                key={`legend-${index}`}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all",
                  hiddenSectors.includes(entry.name)
                    ? "opacity-50 bg-gray-100 dark:bg-gray-700"
                    : "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                )}
                onClick={() => handleLegendClick(entry.name)}
              >
                <span 
                  className="inline-block w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm font-medium whitespace-nowrap">{entry.name}: {entry.value}%</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie 
                data={visibleData} 
                cx="50%" 
                cy="50%" 
                innerRadius={70}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={false}
                labelLine={false}
              >
                {visibleData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[gasDistribution.findIndex(item => item.name === entry.name) % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => [`${value}%`, 'Percentage']}
                contentStyle={getTooltipStyle()}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </AnimatedCard>
  );
};

export default GasDistributionPieChart;
